import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import AuthModal from "@/components/auth/AuthModal";
import MarketingDialog from "@/components/marketing/MarketingDialog";

/**
 * The directory's account prompt (#1050, decision F6). Compare, Save and
 * Message need an account; on a signed-out directory card each opens this
 * prompt, which says what the account unlocks and hands off to the existing
 * AuthModal — in sign-up mode, through its public props only (AuthModal's own
 * behaviour is unchanged). "I already have an account" opens it in sign-in.
 *
 * TODO(#1051): the site-wide account prompts and the retirement of the agent
 * waitlist live in #1051 (auth surface, founder). When that lands, this prompt
 * should use whatever shared prompt it introduces.
 *
 * Both dialogs mount only once opened, so the prerender never renders them.
 */

export type DirectoryAction = "compare" | "save" | "message";

const ACTION_COPY: Record<DirectoryAction, { title: string; lead: (name: string) => string }> = {
  compare: {
    title: "Create a free account to compare brokerages",
    lead: (name) => `Put ${name} side by side with the other brokerages you're weighing.`,
  },
  save: {
    title: "Create a free account to save brokerages",
    lead: (name) => `Keep ${name} on a shortlist you can come back to.`,
  },
  message: {
    title: "Create a free account to message brokerages",
    lead: (name) => `Start a private conversation with ${name}.`,
  },
};

export const ACCOUNT_UNLOCKS =
  "A free agent account also unlocks compensation model and fee insights, conversations with brokerages, and the feed.";

type OpenPrompt = (action: DirectoryAction, orgName: string) => void;

const PromptContext = createContext<OpenPrompt | null>(null);

export function DirectoryAccountPromptProvider({ children }: { children: ReactNode }) {
  const [prompt, setPrompt] = useState<{ action: DirectoryAction; orgName: string } | null>(null);
  const [auth, setAuth] = useState<"signup" | "signin" | null>(null);

  const open = useCallback<OpenPrompt>((action, orgName) => setPrompt({ action, orgName }), []);
  const handOff = (mode: "signup" | "signin") => {
    setPrompt(null);
    setAuth(mode);
  };

  const copy = prompt ? ACTION_COPY[prompt.action] : null;

  return (
    <PromptContext.Provider value={useMemo(() => open, [open])}>
      {children}
      {prompt && copy && (
        <MarketingDialog
          open
          size="narrow"
          onOpenChange={(next) => !next && setPrompt(null)}
          title={copy.title}
          description={copy.lead(prompt.orgName)}
        >
          <p className="mk-body">{ACCOUNT_UNLOCKS}</p>
          <div className="mt-6 flex flex-col gap-3">
            <button type="button" className="mk-btn mk-btn--primary mk-btn--block" onClick={() => handOff("signup")}>
              Create free account
            </button>
            <button type="button" className="mk-btn mk-btn--outline mk-btn--block" onClick={() => handOff("signin")}>
              I already have an account
            </button>
          </div>
        </MarketingDialog>
      )}
      {auth && (
        <AuthModal
          isOpen
          appearance="marketing"
          onClose={() => setAuth(null)}
          defaultMode={auth}
          allowSignup
        />
      )}
    </PromptContext.Provider>
  );
}

/** Opens the prompt; null outside a DirectoryAccountPromptProvider. */
export function useDirectoryAccountPrompt(): OpenPrompt | null {
  return useContext(PromptContext);
}
